import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Skeleton } from 'boneyard-js/react'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { ColumnVisibilityToggle } from '../../../components/ColumnVisibilityToggle'
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DatePicker } from '../../../components/ui/date-picker'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  type ColumnVisibilityDef,
  useColumnVisibility,
} from '../../../hooks/useColumnVisibility'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges'
import { supabase } from '../../../lib/supabase'
import type {
  ContractFormValues,
  ContractRow,
  RoomOption,
  TenantOption,
} from './interfaces/Contract'

const PAGE_SIZE = 20

const HIDDEN_COLS_KEY = 'contracts-hidden-cols'

const COLUMN_DEFS: ColumnVisibilityDef[] = [
  { id: 'tenant', label: 'Tenant', alwaysVisible: true },
  { id: 'room_number', label: 'Room' },
  { id: 'start_date', label: 'Start' },
  { id: 'movein_date', label: 'Move-in' },
  { id: 'end_date', label: 'End' },
  { id: 'status', label: 'Status' },
  { id: 'insurance_amount', label: 'Insurance' },
  { id: 'prepaid_amount', label: 'Prepaid' },
  { id: 'deposit_amount', label: 'Deposit' },
]

type SortColumn =
  | 'created_at'
  | 'room_number'
  | 'start_date'
  | 'movein_date'
  | 'end_date'
  | 'status'
  | 'insurance_amount'
  | 'prepaid_amount'
  | 'deposit_amount'

interface SortState {
  column: SortColumn
  ascending: boolean
}

const contractSchema = z
  .object({
    tenant_id: z.string().uuid('Tenant is required'),
    room_number: z.string().min(1, 'Room is required'),
    insurance_amount: z.coerce.number().min(0),
    prepaid_amount: z.coerce.number().min(0),
    deposit_amount: z.coerce.number().min(0),
    start_date: z.string().min(1, 'Start date is required'),
    movein_date: z.string().min(1, 'Move-in date is required'),
    end_date: z.string().optional().nullable(),
    status: z.enum(['active', 'ended']),
    remark: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.status === 'ended' && !data.end_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date is required when status is "ended"',
        path: ['end_date'],
      })
    }
  })

const today = () => new Date().toISOString().split('T')[0]

const emptyForm = (): ContractFormValues => ({
  tenant_id: '',
  room_number: '',
  insurance_amount: '0',
  prepaid_amount: '0',
  deposit_amount: '0',
  start_date: today(),
  movein_date: today(),
  end_date: '',
  status: 'active',
  remark: '',
})

const rowToForm = (row: ContractRow): ContractFormValues => ({
  tenant_id: row.tenant_id,
  room_number: row.room_number,
  insurance_amount: String(row.insurance_amount),
  prepaid_amount: String(row.prepaid_amount),
  deposit_amount: String(row.deposit_amount),
  start_date: row.start_date,
  movein_date: row.movein_date,
  end_date: row.end_date ?? '',
  status: row.status as 'active' | 'ended',
  remark: row.remark ?? '',
})

export default function ContractsPage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const showHeader = searchParams.get('header') !== 'hide'
  const [showForm, setShowForm] = useState(false)
  const [editingContract, setEditingContract] = useState<ContractRow | null>(
    null,
  )
  const [form, setForm] = useState<ContractFormValues>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [showDeleted, setShowDeleted] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [roomSearch, setRoomSearch] = useState('')
  const [sortState, setSortState] = useState<SortState>({
    column: 'room_number',
    ascending: true,
  })
  const debouncedRoomSearch = useDebouncedValue(roomSearch, 500)
  const normalizedRoomSearch = debouncedRoomSearch.trim()

  const handleSort = (column: SortColumn) => {
    setSortState((prev) =>
      prev.column === column
        ? { column, ascending: !prev.ascending }
        : { column, ascending: true },
    )
  }

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return ' ⇅'
    return sortState.ascending ? ' ↑' : ' ↓'
  }

  const {
    ref: colsDropdownRef,
    open: colsOpen,
    setOpen: setColsOpen,
    hiddenCount,
    isVisible,
    toggleColumn,
  } = useColumnVisibility(HIDDEN_COLS_KEY, COLUMN_DEFS)

  const col = isVisible

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        'contracts',
        showDeleted,
        sortState.column,
        sortState.ascending,
        normalizedRoomSearch,
      ],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const from = pageParam
        const to = from + PAGE_SIZE - 1
        let query = supabase
          .from('contracts')
          .select('*')
          .order(sortState.column, { ascending: sortState.ascending })

        if (normalizedRoomSearch) {
          query = query.eq('room_number', normalizedRoomSearch)
        }

        const { data: rows, error } = showDeleted
          ? await query.not('deleted_at', 'is', null).range(from, to)
          : await query.is('deleted_at', null).range(from, to)
        if (error) throw error
        return rows as ContractRow[]
      },
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.length < PAGE_SIZE ? undefined : lastPageParam + PAGE_SIZE,
    })

  const contracts: ContractRow[] = data?.pages.flat() ?? []

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const sentinelRef = useInfiniteScroll(onLoadMore, Boolean(hasNextPage))

  const { data: tenants = [] } = useQuery<TenantOption[]>({
    queryKey: ['tenants', 'options'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .order('first_name')
        .order('last_name')
      if (error) throw error
      return rows as TenantOption[]
    },
  })

  const { data: rooms = [] } = useQuery<RoomOption[]>({
    queryKey: ['rooms', 'options'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('rooms')
        .select('room_number, status')
        .order('room_number')
      if (error) throw error
      return rows as RoomOption[]
    },
  })

  const createMutation = useMutation({
    mutationFn: async (values: ContractFormValues) => {
      const parsed = contractSchema.parse(values)
      const { error } = await supabase.from('contracts').insert({
        ...parsed,
        end_date: parsed.end_date || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      setShowForm(false)
      setForm(emptyForm())
      setSubmitError(null)
    },
    onError: (err: Error) => {
      setSubmitError(
        err.message.includes('contracts_ended_status_requires_end_date')
          ? 'End date is required when status is "ended".'
          : `Save failed: ${err.message}`,
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id: string
      values: ContractFormValues
    }) => {
      const parsed = contractSchema.parse(values)
      const { error } = await supabase
        .from('contracts')
        .update({ ...parsed, end_date: parsed.end_date || null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      setShowForm(false)
      setEditingContract(null)
      setForm(emptyForm())
      setSubmitError(null)
    },
    onError: (err: Error) => {
      setSubmitError(
        err.message.includes('contracts_ended_status_requires_end_date')
          ? 'End date is required when status is "ended".'
          : `Save failed: ${err.message}`,
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contracts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      setDeleteTarget(null)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('contracts')
        .update({ deleted_at: null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      setRestoreError(null)
    },
    onError: (err: Error) => {
      setRestoreError(
        err.message.includes('contracts_one_active_per_room')
          ? 'Cannot restore: this room already has an active contract.'
          : `Restore failed: ${err.message}`,
      )
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = contractSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        fieldErrors[issue.path[0] as string] = issue.message
      }
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    if (editingContract) {
      updateMutation.mutate({ id: editingContract.id, values: form })
    } else {
      createMutation.mutate(form)
    }
  }

  const openCreate = () => {
    setEditingContract(null)
    setForm(emptyForm())
    setErrors({})
    setSubmitError(null)
    setShowForm(true)
  }

  const openEdit = (contract: ContractRow) => {
    setEditingContract(contract)
    setForm(rowToForm(contract))
    setErrors({})
    setSubmitError(null)
    setShowForm(true)
  }

  const isFormDirty = useMemo((): boolean => {
    if (!showForm) return false
    if (editingContract) {
      const orig = rowToForm(editingContract)
      return (
        form.tenant_id !== orig.tenant_id ||
        form.room_number !== orig.room_number ||
        form.insurance_amount !== orig.insurance_amount ||
        form.prepaid_amount !== orig.prepaid_amount ||
        form.deposit_amount !== orig.deposit_amount ||
        form.start_date !== orig.start_date ||
        form.movein_date !== orig.movein_date ||
        form.end_date !== orig.end_date ||
        form.status !== orig.status ||
        form.remark !== orig.remark
      )
    }
    const empty = emptyForm()
    return (
      form.tenant_id !== empty.tenant_id ||
      form.room_number !== empty.room_number ||
      form.insurance_amount !== empty.insurance_amount ||
      form.prepaid_amount !== empty.prepaid_amount ||
      form.deposit_amount !== empty.deposit_amount ||
      form.start_date !== empty.start_date ||
      form.movein_date !== empty.movein_date ||
      form.end_date !== empty.end_date ||
      form.status !== empty.status ||
      form.remark !== empty.remark
    )
  }, [showForm, form, editingContract])

  const { isBlocked, proceed, reset } = useUnsavedChanges(isFormDirty)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false)

  const closeForm = () => {
    setShowForm(false)
    setEditingContract(null)
    setForm(emptyForm())
    setErrors({})
    setSubmitError(null)
  }

  const handleCancelForm = (): void => {
    if (isFormDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const tenantLabel = (contract: ContractRow) => {
    const t = tenants.find((tenant) => tenant.id === contract.tenant_id)
    if (t) {
      return (
        `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim() ||
        contract.tenant_id.slice(0, 8)
      )
    }
    return contract.tenant_id.slice(0, 8)
  }

  const tenantOptionLabel = (t: TenantOption) =>
    `${t.first_name ?? ''} ${t.last_name ?? ''}`.trim() || t.id.slice(0, 8)

  function renderHeader() {
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-900 text-lg">Contracts</h2>
            <p className="text-gray-500 text-xs">
              {contracts.length} contract{contracts.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={openCreate}>+ New Contract</Button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
          <ColumnVisibilityToggle
            title="Columns"
            columns={COLUMN_DEFS}
            isOpen={colsOpen}
            hiddenCount={hiddenCount}
            wrapperRef={colsDropdownRef}
            isVisible={isVisible}
            onToggle={toggleColumn}
            onToggleOpen={() => setColsOpen((o) => !o)}
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="room-search" className="text-xs">
              Search:
            </Label>
            <Input
              id="room-search"
              type="search"
              placeholder="101"
              value={roomSearch}
              onChange={(e) => setRoomSearch(e.target.value)}
              className="h-7 w-28 text-xs"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowDeleted((v) => !v)
              setRestoreError(null)
            }}
            className={
              showDeleted ? 'border-red-300 bg-red-50 text-red-700' : ''
            }
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button>
        </div>

        {restoreError && (
          <div className="mb-3 rounded-md bg-red-50 px-4 py-2 text-red-700 text-sm">
            {restoreError}
            <button
              type="button"
              onClick={() => setRestoreError(null)}
              className="ml-2 font-medium underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="relative flex h-full flex-col">
      {showHeader && (
        <div className="sticky top-0 z-30 bg-gray-50">{renderHeader()}</div>
      )}

      <Skeleton
        name="admin-contracts"
        loading={isLoading}
        className="flex min-h-0 flex-1 flex-col *:data-boneyard-content:flex *:data-boneyard-content:min-h-0 *:data-boneyard-content:flex-1 *:data-boneyard-content:flex-col"
      >
        <Card className="min-h-0 flex-1 overflow-hidden p-0">
          <div className="h-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="sticky top-0 z-20 bg-gray-50">
                  <th className="px-4 py-3 text-left font-medium text-gray-500">
                    Tenant
                  </th>
                  {(
                    [
                      { key: 'room_number', label: 'Room', align: 'left' },
                      { key: 'start_date', label: 'Start', align: 'left' },
                      { key: 'movein_date', label: 'Move-in', align: 'left' },
                      { key: 'end_date', label: 'End', align: 'left' },
                      { key: 'status', label: 'Status', align: 'left' },
                      {
                        key: 'insurance_amount',
                        label: 'Insurance',
                        align: 'right',
                      },
                      {
                        key: 'prepaid_amount',
                        label: 'Prepaid',
                        align: 'right',
                      },
                      {
                        key: 'deposit_amount',
                        label: 'Deposit',
                        align: 'right',
                      },
                    ] as {
                      key: SortColumn
                      label: string
                      align: 'left' | 'right'
                    }[]
                  )
                    .filter(({ key }) => col(key))
                    .map(({ key, label, align }) => (
                      <th
                        key={key}
                        className={`px-4 py-3 font-medium text-gray-500 text-${
                          align
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(key)}
                          className={`inline-flex cursor-pointer items-center gap-0.5 hover:text-gray-900 ${
                            sortState.column === key ? 'text-gray-900' : ''
                          }`}
                        >
                          {label}
                          <span className="text-xs">{sortIndicator(key)}</span>
                        </button>
                      </th>
                    ))}
                  <th className="px-4 py-3 text-left font-medium text-gray-500">
                    Remark
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {contracts.map((contract) => (
                  <tr
                    key={contract.id}
                    className={
                      contract.deleted_at
                        ? 'bg-red-50 opacity-60'
                        : 'hover:bg-gray-50'
                    }
                  >
                    <td
                      className={`px-4 py-3 font-medium ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                    >
                      {tenantLabel(contract)}
                    </td>
                    {col('room_number') && (
                      <td
                        className={`px-4 py-3 font-medium ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                      >
                        {contract.room_number}
                      </td>
                    )}
                    {col('start_date') && (
                      <td
                        className={`px-4 py-3 ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                      >
                        {contract.start_date}
                      </td>
                    )}
                    {col('movein_date') && (
                      <td
                        className={`px-4 py-3 ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                      >
                        {contract.movein_date}
                      </td>
                    )}
                    {col('end_date') && (
                      <td
                        className={`px-4 py-3 ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                      >
                        {contract.end_date ?? '—'}
                      </td>
                    )}
                    {col('status') && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 font-semibold text-xs ${
                            contract.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          } ${contract.deleted_at ? 'opacity-50' : ''}`}
                        >
                          {contract.status}
                        </span>
                      </td>
                    )}
                    {col('insurance_amount') && (
                      <td className="px-4 py-3 text-right">
                        {Number(contract.insurance_amount).toLocaleString()}
                      </td>
                    )}
                    {col('prepaid_amount') && (
                      <td className="px-4 py-3 text-right">
                        {Number(contract.prepaid_amount).toLocaleString()}
                      </td>
                    )}
                    {col('deposit_amount') && (
                      <td className="px-4 py-3 text-right">
                        {Number(contract.deposit_amount).toLocaleString()}
                      </td>
                    )}
                    <td
                      className={`px-4 py-3 text-gray-600 text-sm ${contract.deleted_at ? 'text-gray-400 line-through' : ''}`}
                    >
                      {contract.remark ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {contract.deleted_at ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => restoreMutation.mutate(contract.id)}
                          disabled={restoreMutation.isPending}
                          className="text-green-700 hover:bg-green-50"
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(contract)}
                            className="mr-2"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(contract.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div ref={sentinelRef} className="h-6" />
          {isFetchingNextPage && (
            <div className="py-2 text-center text-gray-500 text-sm">
              Loading more…
            </div>
          )}
        </Card>
      </Skeleton>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card
            className="w-full max-w-lg overflow-y-auto p-6"
            style={{ maxHeight: '90vh' }}
          >
            <h3 className="mb-4 font-semibold text-lg">
              {editingContract ? 'Edit Contract' : 'New Contract'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {submitError && (
                <div className="rounded-md bg-red-50 px-4 py-2 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="tenant_id">Tenant</Label>
                  <select
                    id="tenant_id"
                    title="Tenant"
                    value={form.tenant_id}
                    onChange={(e) =>
                      setForm({ ...form, tenant_id: e.target.value })
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select tenant</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {tenantOptionLabel(t)}
                      </option>
                    ))}
                  </select>
                  {errors.tenant_id && (
                    <p className="mt-1 text-red-500 text-xs">
                      {errors.tenant_id}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="room_number">Room</Label>
                  <select
                    id="room_number"
                    title="Room"
                    value={form.room_number}
                    onChange={(e) =>
                      setForm({ ...form, room_number: e.target.value })
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select room</option>
                    {rooms.map((r) => (
                      <option key={r.room_number} value={r.room_number}>
                        {r.room_number} ({r.status})
                      </option>
                    ))}
                  </select>
                  {errors.room_number && (
                    <p className="mt-1 text-red-500 text-xs">
                      {errors.room_number}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(['start_date', 'movein_date', 'end_date'] as const).map(
                  (key) => (
                    <div key={key}>
                      <Label htmlFor={key}>
                        {key === 'start_date'
                          ? 'Start Date'
                          : key === 'movein_date'
                            ? 'Move-in Date'
                            : form.status === 'ended'
                              ? 'End Date (required)'
                              : 'End Date (optional)'}
                      </Label>
                      <DatePicker
                        id={key}
                        value={form[key]}
                        onValueChange={(value) =>
                          setForm({ ...form, [key]: value })
                        }
                        className="mt-1 w-full text-sm"
                      />
                      {errors[key] && (
                        <p className="mt-1 text-red-500 text-xs">
                          {errors[key]}
                        </p>
                      )}
                    </div>
                  ),
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    'insurance_amount',
                    'prepaid_amount',
                    'deposit_amount',
                  ] as const
                ).map((key) => (
                  <div key={key}>
                    <Label htmlFor={key}>
                      {key === 'insurance_amount'
                        ? 'Insurance (฿)'
                        : key === 'prepaid_amount'
                          ? 'Prepaid (฿)'
                          : 'Deposit (฿)'}
                    </Label>
                    <Input
                      id={key}
                      type="number"
                      min="0"
                      step="0.01"
                      value={form[key]}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.value })
                      }
                    />
                  </div>
                ))}
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  title="Status"
                  value={form.status}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value as 'active' | 'ended',
                    })
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="ended">Ended</option>
                </select>
              </div>

              <div>
                <Label htmlFor="remark">Remark</Label>
                <Input
                  id="remark"
                  type="text"
                  placeholder="Optional note"
                  value={form.remark ?? ''}
                  onChange={(e) => setForm({ ...form, remark: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelForm}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingContract ? 'Save Changes' : 'Create Contract'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={isBlocked}
        title="Unsaved Changes"
        description="You have unsaved changes in the contract form. Are you sure you want to leave without saving? Your changes will be lost."
        confirmText="Leave without saving"
        cancelText="Stay on page"
        onConfirm={proceed}
        onCancel={reset}
      />

      <UnsavedChangesDialog
        isOpen={showDiscardConfirm}
        title="Discard Contract Changes?"
        description="You have unsaved changes in this form. Are you sure you want to discard them?"
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={() => {
          setShowDiscardConfirm(false)
          closeForm()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="mb-2 font-semibold text-lg">Delete Contract</h3>
            <p className="mb-6 text-gray-600 text-sm">
              Are you sure you want to archive this contract? You can restore it
              later from the deleted contracts view.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => deleteMutation.mutate(deleteTarget)}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                Archive
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
